import { FC, ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import {
  Call,
  ParticipantView,
  StreamVideoParticipant,
  useCall,
  useCallStateHooks,
} from '@stream-io/video-react-sdk';
import { CameraVideo, CameraVideoOff, Gear, Mic, MicMute, PeopleFill, ShieldLockFill, TelephoneX } from 'react-bootstrap-icons';
import Button from 'react-bootstrap/Button';
import ConfirmationModal from '../ConfirmationModal';
import { CallRole } from '../../../../shared/dtos/joinCall.dto';
import { useAuthFetchFromRestAPI } from '../../util/api';
import { AUTH0_ORGANIZATION_ID } from '../../config';
import i18next from 'i18next';
import NetworkQualityPill from './NetworkQualityPill';
import DeviceSettingsPanel from './DeviceSettingsPanel';
import ParticipantUIView from './ParticipantUIView';
import SpeakingWhileMutedToast from './SpeekingWhileMutedToast';

type CallViewProps = {
  onLeaveCall?: () => void;
  role: CallRole;
  roomCode: string;
  callStartedAt: Date;
};

const formatDuration = (seconds: number): string => {
  const hours: number = Math.floor(seconds / 3600);
  const minutes: number = Math.floor((seconds % 3600) / 60);
  const remaining: number = seconds % 60;
  
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${remaining.toString().padStart(2, '0')}`;
};

const CallView: FC<CallViewProps> = ({ onLeaveCall, role, roomCode, callStartedAt }: CallViewProps): ReactElement => {
  const call: Call | undefined = useCall();
  const { authFetch } = useAuthFetchFromRestAPI();
  const { useParticipants, useLocalParticipant, useRemoteParticipants, useCameraState, useMicrophoneState } = useCallStateHooks();

  const participants: StreamVideoParticipant[] = useParticipants();
  const remoteParticipants: StreamVideoParticipant[] = useRemoteParticipants();
  const localParticipant: StreamVideoParticipant | undefined = useLocalParticipant();
  const cameraState = useCameraState();
  const microphoneState = useMicrophoneState();

  const isHost: boolean = role === 'therapist';
  const [showEndCallConfirm, setShowEndCallConfirm] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const settingsRef = useRef<HTMLDivElement>(null);

  const [durationInSeconds, setDurationInSeconds] = useState<number>(
    () => Math.floor((Date.now() - callStartedAt.getTime()) / 1000),
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setDurationInSeconds(Math.floor((Date.now() - callStartedAt.getTime()) / 1000));
    }, 1000);
    return (): void => clearInterval(interval);
  }, [callStartedAt]);

  useEffect(() => {
    const handler = (e: MouseEvent): void => {
      if (settingsRef.current && !settingsRef.current.contains(e.target as Node)) {
        setShowSettings(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return (): void => document.removeEventListener('mousedown', handler);
  }, []);

  const hasRemoteParticipant: boolean = remoteParticipants.length > 0;

  const mainParticipant: StreamVideoParticipant | undefined = useMemo(
    () => (hasRemoteParticipant ? remoteParticipants[0] : localParticipant),
    [hasRemoteParticipant, remoteParticipants, localParticipant],
  );

  const handleLeave = (): void => {
    if (isHost && hasRemoteParticipant) {
      setShowEndCallConfirm(true);
      return;
    }
    onLeaveCall?.();
  };

  const handleEndCall = async (): Promise<void> => {
    const query : URLSearchParams= new URLSearchParams({ tenant: AUTH0_ORGANIZATION_ID });
    const url: string = `/api/v1/call/end/${encodeURIComponent(roomCode)}?${query.toString()}`;
    
    setShowEndCallConfirm(false);
    await authFetch(url, { method: 'POST' });
    onLeaveCall?.();
  };

  const handleToggleCamera = async (): Promise<void> => {
    if (!call) return;
    if (cameraState.camera.enabled) {
      await call.camera.disable().catch(() => undefined);
    } else {
      await call.camera.enable().catch(() => undefined);
    }
  };

  const handleToggleMicrophone = async (): Promise<void> => {
    if (!call) return;
    if (microphoneState.microphone.enabled) {
      await call.microphone.disable().catch(() => undefined);
    } else {
      await call.microphone.enable().catch(() => undefined);
    }
  };

  return (
    <div className="w-100 d-flex flex-column flex-grow-1 overflow-hidden" style={{ minHeight: 0, backgroundColor: '#05080d' }}>

      <div className="px-3 pt-2 pb-2">
        <div
          className="d-flex align-items-center justify-content-between px-3"
          style={{ backgroundColor: '#0b1118', borderRadius: '1rem', minHeight: '48px' }}
        >
          <div className="d-flex align-items-center gap-3 text-white">
            <img
              src="/themes/physio/logo_1.png"
              alt="Enlivio"
              style={{ height: '26px', width: 'auto', filter: 'brightness(0) invert(1)' }}
            />
          </div>

          <div className="d-flex align-items-center gap-2 text-white">
            <HeaderPill icon={<PeopleFill size={14} />} text={String(participants.length)} />
            <HeaderPill icon={<ShieldLockFill size={14} />} text={formatDuration(durationInSeconds)} />
            {localParticipant && <NetworkQualityPill participant={localParticipant} />}
          </div>
        </div>
      </div>

      <div className="flex-grow-1" style={{ minHeight: 0 }}>
        <div
          className="position-relative w-100 h-100 overflow-hidden d-flex justify-content-center"
          style={{ backgroundColor: '#1c2b3f' }}
        >
          {mainParticipant && (
            <div className="w-100 h-100 d-flex align-items-center justify-content-center overflow-hidden">
              <ParticipantView
                className="w-100 h-100 text-white"
                participant={mainParticipant}
                ParticipantViewUI={<ParticipantUIView />}
              />
            </div>
          )}

          {localParticipant && hasRemoteParticipant && (
            <div
              className="position-absolute"
              style={{
                bottom: '16px',
                right: '16px',
                width: 'clamp(140px, 20%, 220px)',
                aspectRatio: '16 / 10',
                borderRadius: '0.875rem',
                overflow: 'hidden',
                border: '1px solid rgba(255,255,255,0.1)',
                backgroundColor: '#0f1722',
                boxShadow: '0 10px 28px rgba(0,0,0,0.35)',
              }}
            >
              <ParticipantView
                className="text-white w-100 h-100"
                participant={localParticipant}
                muteAudio
                ParticipantViewUI={<ParticipantUIView />}
              />
            </div>
          )}
        </div>
      </div>

      <SpeakingWhileMutedToast />

      <div className="py-2">
        <div className="d-flex justify-content-center">
          <div
            className="position-relative d-flex align-items-center gap-2 px-3 py-2"
            style={{ backgroundColor: 'rgba(11,17,24,0.92)', borderRadius: '999px', backdropFilter: 'blur(8px)' }}
          >
            <IconButton
              icon={microphoneState.microphone.enabled ? <Mic size={18} /> : <MicMute size={18} />}
              enabled={microphoneState.microphone.enabled}
              onClick={() => void handleToggleMicrophone()}
            />

            <IconButton
              icon={cameraState.camera.enabled ? <CameraVideo size={18} /> : <CameraVideoOff size={18} />}
              enabled={cameraState.camera.enabled}
              onClick={() => void handleToggleCamera()}
            />

            <div ref={settingsRef} className="position-relative">
              <IconButton
                icon={<Gear size={18} />}
                enabled={true}
                onClick={() => setShowSettings((value: boolean): boolean => !value)}
              />
              {showSettings && <DeviceSettingsPanel onClose={() => setShowSettings(false)} />}
            </div>

            <Button
              variant="danger"
              className="rounded-pill px-4 fw-semibold"
              style={{ minHeight: '44px', backgroundColor: '#e85f5c', borderColor: '#e85f5c' }}
              onClick={handleLeave}
            >
              <TelephoneX className="me-2" />
              {i18next.t('generic.end')}
            </Button>
          </div>
        </div>
      </div>

      <ConfirmationModal
        show={showEndCallConfirm}
        title={i18next.t('callPage.endCallTitle')}
        body={i18next.t('callPage.endCallBody')}
        buttonPositive={i18next.t('callPage.endCallConfirm')}
        buttonNegative={i18next.t('generic.cancel')}
        onPositive={() => void handleEndCall()}
        onNegative={() => setShowEndCallConfirm(false)}
      />
    </div>
  );
};


type HeaderPillProps = { icon: ReactElement; text: string };

const HeaderPill: FC<HeaderPillProps> = ({ icon, text }: HeaderPillProps): ReactElement => (
  <div
    className="d-flex align-items-center gap-2 px-3"
    style={{ backgroundColor: '#122033', borderRadius: '999px', minHeight: '34px', color: '#ffffff' }}
  >
    {icon}
    <span className="fw-semibold small">{text}</span>
  </div>
);

type IconButtonProps = { icon: ReactElement; onClick?: () => void; enabled?: boolean };

const IconButton: FC<IconButtonProps> = ({ icon, onClick, enabled = true }: IconButtonProps): ReactElement => (
  <Button
    variant="link"
    className="d-inline-flex align-items-center justify-content-center rounded-circle p-0 text-decoration-none"
    style={{ width: '44px', height: '44px', backgroundColor: enabled ? '#122033' : '#7b2e37', color: '#ffffff', border: 'none' }}
    onClick={onClick}
  >
    {icon}
  </Button>
);

export default CallView;